import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { toast } from 'react-toastify';
import Loader from '../../components/common/Loader';
import NoData from '../../components/common/NoData';
import { listAssignedSurveys } from '../../services/userService';

function AssignedSurveys() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((s) => String(s.title || '').toLowerCase().includes(q));
  }, [rows, query]);

  const fetchList = async () => {
    setLoading(true);
    try {
      const resp = await listAssignedSurveys();
      if (!resp?.success) {
        toast.error(resp?.message || 'Failed to load surveys');
        setRows([]);
        return;
      }
      setRows(Array.isArray(resp.data) ? resp.data : []);
    } catch (e) {
      toast.error('Failed to load surveys');
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchList();
  }, []);

  const badge = (s) => {
    const st = String(s.assignment_status || '').toLowerCase();
    if (st === 'submitted') {
      return <span className="text-xs font-semibold px-2.5 py-1 rounded-lg bg-emerald-50 text-emerald-700 border border-emerald-200">Submitted</span>;
    }
    if (st === 'assigned') {
      return <span className="text-xs font-semibold px-2.5 py-1 rounded-lg bg-amber-50 text-amber-700 border border-amber-200">Pending</span>;
    }
    return <span className="text-xs font-semibold px-2.5 py-1 rounded-lg bg-gray-50 text-gray-700 border border-gray-200">{s.assignment_status || '—'}</span>;
  };

  if (loading) return <Loader message="Loading surveys..." />;

  return (
    <div className="w-full">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-semibold text-gray-900">Surveys</h1>
          <p className="text-sm text-gray-500 mt-1">Complete assigned surveys. Your responses help improve the learning experience.</p>
        </div>
        <button
          type="button"
          onClick={fetchList}
          className="inline-flex items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm font-semibold text-gray-800 hover:bg-gray-50"
        >
          <Icon icon="fluent:arrow-clockwise-24-regular" width="18" height="18" />
          Refresh
        </button>
      </div>

      <div className="mb-4">
        <div className="relative">
          <Icon icon="fluent:search-24-regular" width="18" height="18" className="text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="w-full rounded-2xl border border-gray-200 bg-white pl-10 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
            placeholder="Search surveys..."
          />
        </div>
      </div>

      {filtered.length === 0 ? (
        <NoData message={rows.length === 0 ? 'No surveys assigned yet' : 'No matching surveys'} />
      ) : (
        <div className="grid grid-cols-1 gap-3">
          {filtered.map((s) => (
            <button
              key={s.id}
              type="button"
              onClick={() => navigate(`/user/survey/${s.id}`)}
              className="text-left w-full rounded-2xl border border-gray-100 bg-white shadow-sm hover:shadow-md transition p-4"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center">
                      <Icon icon="fluent:clipboard-task-list-ltr-24-regular" width="20" height="20" className="text-primary" />
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm sm:text-base font-semibold text-gray-900 truncate">{s.title}</div>
                      {s.description ? <div className="text-xs text-gray-500 truncate">{s.description}</div> : null}
                    </div>
                  </div>
                </div>
                <div className="flex-shrink-0">{badge(s)}</div>
              </div>

              <div className="mt-3 flex items-center justify-between">
                <div className="text-xs text-gray-500">
                  {s.submitted_at ? `Submitted on ${new Date(s.submitted_at).toLocaleDateString()}` : 'Not submitted yet'}
                </div>
                <div className="text-xs font-semibold text-primary inline-flex items-center gap-1">
                  Open
                  <Icon icon="fluent:chevron-right-24-regular" width="16" height="16" />
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default AssignedSurveys;
